import { useEffect, useState } from "react";
import { AfiliadoCodigo } from "@/types";
import { ButtonSiguiente, Input } from "@/components/ui";
import { useNavigate } from "react-router-dom";
import { safeFetch } from "@/api/safeFetch";

export default function ResumenAfiliados() {
  const [resultados, setResultados] = useState<AfiliadoCodigo[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [codigoSeleccionado, setCodigoSeleccionado] = useState<string | null>(null);
  const [aplicaciones, setAplicaciones] = useState<any[]>([]);
  const [, setLoadingDetalle] = useState(false);
  const [fechaInicio, setFechaInicio] = useState<string>(""); // yyyy-mm-dd
  const [fechaFin, setFechaFin] = useState<string>("");
  const [month, setMonth] = useState<string>(""); // yyyy-mm
  const [usos, setUsos] = useState<number | null>(null);
  const [loadingUsos, setLoadingUsos] = useState(false);
  const [importe] = useState<number>(15);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // ======================
  // Buscar afiliados
  // ======================
  const handleBuscar = async () => {
    const controller = new AbortController();
    const signal = controller.signal;

    setError("");
    setLoading(true);
    setCodigoSeleccionado(null);
    setAplicaciones([]);

    try {
      let url = "/api/afiliados/afiliado-codigos";
      if (busqueda.trim()) {
        url += `?busqueda=${encodeURIComponent(busqueda.trim())}`;
      }

      const res = await safeFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });

      if (!res.ok) throw new Error("No se encontraron resultados");

      const data: AfiliadoCodigo[] = await res.json();

      const getNombreCompleto = (x: AfiliadoCodigo) =>
        `${x.nombre} ${x.apellidos}`.trim();

      data.sort((a, b) =>
        getNombreCompleto(a).localeCompare(getNombreCompleto(b))
      );

      setResultados(data);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "Ocurrió un error");
        setResultados([]);
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  };


  // ======================
  // Ver aplicaciones de un código
  // ======================
  const handleVerAplicaciones = async (codigo: string) => {
    const controller = new AbortController();
    const signal = controller.signal;

    setCodigoSeleccionado(codigo);
    setLoadingDetalle(true);
    setError("");

    try {
      const res = await safeFetch(
        `/api/afiliados/aplicaciones-por-codigo/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );

      if (!res.ok) throw new Error("No se pudieron cargar las aplicaciones");

      const data = await res.json();
      setAplicaciones(data);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "Error cargando aplicaciones");
        setAplicaciones([]);
      }
    } finally {
      setLoadingDetalle(false);
    }

    return () => controller.abort();
  };



  // Función para pedir la cuenta de usos
  const handleContarUsos = async (codigo: string) => {
    const controller = new AbortController();
    const signal = controller.signal;

    setLoadingUsos(true);
    setUsos(null);
    setError("");

    try {
      let url = `/api/afiliados/usos-codigo/${encodeURIComponent(codigo)}`;

      if (month) {
        url += `?month=${encodeURIComponent(month)}`;
      } else if (fechaInicio && fechaFin) {
        const startIso = `${fechaInicio}T00:00:00Z`;
        const endIso = `${fechaFin}T23:59:59Z`;
        url += `?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`;
      }

      const res = await safeFetch(url, { headers: { Authorization: `Bearer ${token}` }, signal });
      if (!res.ok) throw new Error("No se pudo contar los usos");

      const json = await res.json();
      setUsos(json.usos ?? 0);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "Error contando usos");
      }
    } finally {
      setLoadingUsos(false);
    }

    return () => controller.abort();
  };


  useEffect(() => {
    return () => {
      setResultados([]);
      setBusqueda("");
      setError("");
      setLoading(false);
      setCodigoSeleccionado(null);
      setAplicaciones([]);
      setLoadingDetalle(false);
      setFechaInicio("");
      setFechaFin("");
      setMonth("");
      setUsos(null);
      setLoadingUsos(false);
    };
  }, []);



  /* 
    const afiliadoNombre = aplicaciones.length > 0 ? aplicaciones[0].trabajador_nombre : "—"; */


  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative">

      {/* 🔙 Botón volver arriba izquierda */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/afiliados")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>


      <div className="w-full max-w-6xl bg-zinc-400 p-4 sm:p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-blue-700 flex flex-col gap-6">
        <h2 className="text-xl sm:text-2xl text-center font-bold">
          Resumen de Códigos de Afiliado
        </h2>

        {/* BUSCADOR */}
        <div className="w-full bg-blue-600 p-4 rounded-xl flex flex-col md:flex-row gap-4 items-stretch border-zinc-200 border border-r-4 border-t-2 shadow-lg shadow-blue-700">
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Introduce DNI o Código"
            className="h-10 px-3 rounded w-full md:w-1/2"
          />

          <ButtonSiguiente
            className="!h-9 px-6 bg-green-600 text-white rounded w-full md:w-1/2"
            onClick={handleBuscar}
            disabled={loading}
          >
            {loading ? "Buscando..." : "Buscar"}
          </ButtonSiguiente>
        </div>

        {error && <p className="text-red-600 text-center">{error}</p>}

        {/* TABLA RESULTADOS */}
        {resultados.length > 0 && (
          <div className="bg-zinc-300 p-4 rounded-lg border shadow overflow-x-auto">
            <table className="min-w-[900px] w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-blue-700 text-white">
                  <th className="p-2 border">Afiliado</th>
                  <th className="p-2 border">DNI</th>
                  <th className="p-2 border">Código</th>
                  <th className="p-2 border">% Desc.</th>
                  <th className="p-2 border">Total</th>
                  <th className="p-2 border">Pagos</th>
                  <th className="p-2 border">Expira</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((r) => (
                  <tr key={r.codigo}>
                    <td className="p-2 border">{r.nombre}</td>
                    <td className="p-2 border">{r.apellidos}</td>
                    <td className="p-2 border">{r.dni}</td>
                    <td
                      className="p-2 border text-blue-700 underline cursor-pointer"
                      onClick={() => handleVerAplicaciones(r.codigo)}
                    >
                      {r.codigo}
                    </td>
                    <td className="p-2 border">{r.porcentaje_descuento}%</td>
                    <td className="p-2 border">{r.total_generado} €</td>
                    <td className="p-2 border">{r.total_pagos}</td>
                    <td className="p-2 border">
                      {r.fecha_expiracion
                        ? new Date(r.fecha_expiracion).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DETALLE */}
        {codigoSeleccionado && (
          <div className="bg-zinc-300 p-4 sm:p-6 rounded-lg border shadow flex flex-col gap-6">

            {/* CABECERA */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h3 className="font-bold text-lg">
                Código: {codigoSeleccionado}
              </h3>

              <div className="text-sm text-gray-700 text-right">
                <p>Usos: <strong>{usos ?? "—"}</strong></p>
                {month && <p className="text-xs">Mes: {month}</p>}
                {fechaInicio && fechaFin && (
                  <p className="text-xs">{fechaInicio} → {fechaFin}</p>
                )}
              </div>
            </div>

            {/* FILTROS */}
            <div className="md:flex grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">

              <label className="text-sm flex-shrink-0">Mes:</label>

              <div className="relative flex-1">
                <Input
                  type="month"
                  value={month}
                  onChange={(e) => { setMonth(e.target.value); setFechaInicio(""); setFechaFin(""); }}
                  className="h-10 px-3 rounded appearance-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                  📅
                </span>
              </div>

              <span className="px-2 flex-shrink-0">Rango:</span>

              <div className="relative flex-1">
                <Input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); setMonth(""); }}
                  className="h-10 px-3 rounded appearance-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                  📅
                </span>
              </div>

              <div className="relative flex-1">
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => { setFechaFin(e.target.value); setMonth(""); }}
                  className="h-10 px-3 rounded appearance-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                  📅
                </span>
              </div>

              <ButtonSiguiente
                onClick={() => handleContarUsos(codigoSeleccionado)}
                className="!h-9"
                disabled={loadingUsos || (!month && !(fechaInicio && fechaFin))}
              >
                {loadingUsos ? "Contando..." : "Mostrar"}
              </ButtonSiguiente>

            </div>


            {/* TABLA APLICACIONES */}
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto border rounded">
              <table className="min-w-[600px] w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-blue-700 text-white">
                    <th className="p-2 border">Afiliado</th>
                    <th className="p-2 border">Fecha</th>
                    <th className="p-2 border">Info</th>
                  </tr>
                </thead>
                <tbody>
                  {aplicaciones.map((a, i) => (
                    <tr key={i} className="hover:bg-blue-100">
                      <td className="p-2 border">{a.trabajador_nombre}</td>
                      <td className="p-2 border">{new Date(a.aplicado_en).toLocaleString()}</td>
                      <td className="p-2 border">{a.profesional_nombre}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            <div className="text-right font-bold text-lg">
              Total: {usos && importe ? (usos * importe).toFixed(2) : "—"} €
            </div>
          </div>
        )}

        {resultados.length === 0 && !error && (
          <p className="text-center text-gray-700">
            No hay resultados para mostrar
          </p>
        )}
      </div>
    </div>

  );
}
